import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { getOrders } from "core/Services/MerchantServices";
import { getUsers } from "core/Services/AdminServices";
import { ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { formatDate } from "utils/DatesUtil";
import { addPayment } from "core/Services/AccountantServices";
import { useTranslation } from "react-i18next";

interface AddPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddPaymentDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: AddPaymentDialogProps) => {
  const { t } = useTranslation();
  const { register, watch } = useForm();
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [isFetchingMerchants, setIsFetchingMerchants] = useState(false);
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleOrderSelection = (orderId: number) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const fetchOrders = async () => {
    const merchantId = watch("merchantId");
    const fromDate = watch("fromDate") || undefined;
    const toDate = watch("toDate") || undefined;
    const orderId = watch("orderId") || undefined;

    if (!merchantId) return;
    try {
      setIsFetchingOrders(true);
      const response = await getOrders({
        merchant_id: merchantId,
        multiple_status: ["shipped", "ship_partially", "handed_off"],
        dateFrom: fromDate,
        dateTo: toDate,
        order: orderId,
        payment: true,
      });
      if (response && response.data) {
        setOrders(response.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Failed to fetch orders", error);
    } finally {
      setIsFetchingOrders(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById("merchant-dropdown");
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setIsFetchingMerchants(true);
        const response = await getUsers({ role: "merchant", getAll: 1 });
        setMerchants(response.data);
      } catch (error) {
        toast.error("Failed to load merchants.");
      } finally {
        setIsFetchingMerchants(false);
      }
    };
    fetchMerchants();
  }, []);

  useEffect(() => {
    const subscription = watch((values) => {
      if (
        values.merchantId ||
        values.fromDate ||
        values.toDate ||
        values.orderId
      ) {
        fetchOrders();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const calculatePayment = (
    total: number,
    deliveryFee: number,
    type: any
  ): String => {
    if (type === "COD") {
      return (total - deliveryFee).toFixed(2);
    } else {
      return (deliveryFee * -1).toFixed(2);
    }
  };
  const handleSubmit = async () => {
    const merchantId = watch("merchantId");
    if (!merchantId) {
      toast.error(t("addPaymentDialog.errors.selectMerchant"));
      setIsDropdownOpen(true);
      return;
    }
    if (selectedOrders.length === 0) {
      toast.error(t("addPaymentDialog.errors.selectOrder"));
      return;
    }

    setIsSubmitting(true);
    try {
      await addPayment({
        merchant_id: merchantId,
        order_ids: selectedOrders,
      });
      toast.success(t("addPaymentDialog.success.paymentSubmitted"));
      onSuccess();
      onClose();
      setSelectedOrders([]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          t("addPaymentDialog.errors.failedToSubmit")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">
            {t("addPaymentDialog.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div id="merchant-dropdown" className="relative">
            <input
              type="text"
              placeholder={t("addPaymentDialog.searchMerchant")}
              className="w-full p-2 pl-3 pr-10 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={merchantSearch}
              onChange={(e) => setMerchantSearch(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown size={20} className="text-gray-500" />
            </div>
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                {isFetchingMerchants ? (
                  <div className="p-2 text-gray-500">
                    {t("addPaymentDialog.loadingMerchants")}
                  </div>
                ) : merchants.filter((merchant) =>
                    merchant.business_name
                      .toLowerCase()
                      .includes(merchantSearch.toLowerCase())
                  ).length === 0 ? (
                  <div className="p-2 text-gray-500">
                    {t("addPaymentDialog.noMerchants")}
                  </div>
                ) : (
                  merchants
                    .filter((merchant) =>
                      merchant.business_name
                        .toLowerCase()
                        .includes(merchantSearch.toLowerCase())
                    )
                    .map((merchant) => (
                      <div
                        key={merchant.id}
                        className={`p-2 cursor-pointer hover:bg-gray-100 ${
                          watch("merchantId") === merchant.id
                            ? "bg-blue-50"
                            : ""
                        }`}
                        onClick={() => {
                          register("merchantId").onChange({
                            target: { value: merchant.id, name: "merchantId" },
                          });
                          setMerchantSearch(merchant.business_name);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {merchant.business_name}
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
          <FormInput
            name="orderId"
            type="text"
            placeholder={t("addPaymentDialog.searchOrderId")}
            register={register}
            errors={{}}
          />
          <FormInput
            name="fromDate"
            type="date"
            placeholder={t("addPaymentDialog.fromDate")}
            register={register}
            errors={{}}
          />
          <FormInput
            name="toDate"
            type="date"
            placeholder={t("addPaymentDialog.toDate")}
            register={register}
            errors={{}}
          />
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              {t("addPaymentDialog.selectOrders")}
            </p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          orders.length > 0 &&
                          selectedOrders.length === orders.length
                        }
                        onChange={() => {
                          if (selectedOrders.length === orders.length) {
                            setSelectedOrders([]);
                          } else {
                            setSelectedOrders(orders.map((order) => order.id));
                          }
                        }}
                      />
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.orderId")}
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.date")}
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.paymentType")}
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.orderAmount")}
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.deliveryFee")}
                    </th>
                    <th className="p-3 text-left">
                      {t("addPaymentDialog.pay")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isFetchingOrders
                    ? [...Array(5)].map((_, index) => (
                        <tr key={index}>
                          {[...Array(4)].map((_, colIndex) => (
                            <td key={colIndex} className="p-3">
                              <Skeleton width={80} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : orders.map((order) => (
                        <tr key={order.id}>
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => handleOrderSelection(order.id)}
                            />
                          </td>
                          <td className="p-3">{order.id}</td>
                          <td className="p-3">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="p-3">{order.payment_method}</td>
                          <td className="p-3">JOD {order.total.toFixed(2)}</td>
                          <td className="p-3">
                            JOD {order.delivery_fees.toFixed(2)}
                          </td>
                          <td className="p-3">
                            JOD{" "}
                            {calculatePayment(
                              order.total,
                              order.delivery_fees,
                              order.payment_method
                            )}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={onClose} variant="outlined">
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              {t("addPaymentDialog.submitPayment")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentDialog;
